import CloseRounded from "@mui/icons-material/CloseRounded";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardTab from "@mui/icons-material/KeyboardTab";
import MenuRounded from "@mui/icons-material/MenuRounded";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { grey } from "@mui/material/colors";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { PushPin } from "@mui/icons-material";
import isNil from "lodash/isNil";
import PropTypes from "prop-types";
import {
  Fragment,
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import Flx from "../../components/layout/Flx";
import { navSidebarToggle } from "../../redux/actions/navSidebarActions";
import DashboardLogo from "../../assets/DashboardLogo";

// Lazy load heavy components
const DashboardRoot = lazy(() => import("./ui/DashboardRoot"));
const DashboardMain = lazy(() => import("./ui/DashboardMain"));
const DashboardSidebarPane = lazy(() => import("./ui/DashboardSidebarPane"));
const DashboardPanelSvg = lazy(() => import("./assets/DashboardPanelSvg"));

const COLLAPSED_WIDTH = 48;
const EXPANDED_WIDTH = 220;

const getItemStyles = ({ theme, isActive, collapsed }) => ({
  padding: collapsed ? "10px 0" : "10px 8px",
  borderRadius: "4px",
  fontSize: "11px",
  display: "flex",
  gap: "8px",
  alignItems: "center",
  justifyContent: collapsed ? "center" : "flex-start",
  whiteSpace: "nowrap",
  fontFamily: "var(--primaryFont)",
  fontWeight: isActive ? 700 : 500,
  background: isActive ? "#2962ff1f" : "none",
  color: isActive ? theme.palette.primary.main : "inherit",
  borderRight: isActive
    ? `3px solid ${theme.palette.primary.main}`
    : "3px solid transparent",
  width: "100%",
  textDecoration: "none",
  zIndex: 111,
  cursor: "pointer",
  overflow: "hidden",
  minHeight: "36px",
  boxSizing: "border-box",
});

// ============================================================
// DashboardLayout
// ============================================================
const DashboardLayout = ({ open = true, children, topLinks, bottomLinks }) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  return (
    <DashboardRoot isMobile={isMobile}>
      <NavRenderer
        isMobile={isMobile}
        open={open}
        topLinks={topLinks}
        bottomLinks={bottomLinks}
      />
      <DashboardMain>{children}</DashboardMain>
    </DashboardRoot>
  );
};

// ============================================================
// NavRenderer — handles hover-expand logic
// ============================================================
const NavRenderer = ({ isMobile, open, topLinks, bottomLinks }) => {
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (!open) {
      hoverTimeoutRef.current = setTimeout(() => setHoverExpanded(true), 150);
    }
  }, [open]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
    setHoverExpanded(false);
  }, []);

  // Reset hover when sidebar toggles open
  useEffect(() => {
    if (open) setHoverExpanded(false);
  }, [open]);

  if (isMobile) {
    return (
      <MobileNavbar>
        <SidebarContent
          collapsed={false}
          topLinks={topLinks}
          bottomLinks={bottomLinks}
        />
      </MobileNavbar>
    );
  }

  return (
    <>
      {/* Persistent spacer — reserves layout width */}
      <DashboardSidebarPane
        open
        drawerWidth={open ? EXPANDED_WIDTH : COLLAPSED_WIDTH}
        transitionDuration={200}
        sx={{
          "&:hover": {
            background: grey[100],
          },
        }}
      >
        {!hoverExpanded && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <SidebarHeader open={open} />
            <SidebarContent
              collapsed={!open}
              topLinks={topLinks}
              bottomLinks={bottomLinks}
              onMouseLeave={handleMouseLeave}
              onMouseEnter={!open ? handleMouseEnter : undefined}
            />
          </Box>
        )}
      </DashboardSidebarPane>

      {/* Hover overlay — fixed, doesn't affect layout */}
      {!open && (
        <Box
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: EXPANDED_WIDTH,
            height: "100vh",
            bgcolor: "background.paper",
            borderRight: "1px solid",
            borderColor: "divider",
            boxShadow: hoverExpanded ? "4px 0 24px rgba(0, 0, 0, 0.1)" : "none",
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            opacity: hoverExpanded ? 1 : 0,
            transform: hoverExpanded ? "translateX(0)" : "translateX(-12px)",
            pointerEvents: hoverExpanded ? "auto" : "none",
            transition:
              "opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
          }}
        >
          <SidebarHeader open hoverExpanded />
          <SidebarContent
            collapsed={false}
            topLinks={topLinks}
            bottomLinks={bottomLinks}
          />
        </Box>
      )}
    </>
  );
};

// ============================================================
// SidebarHeader — logo + toggle button
// ============================================================
const SidebarHeader = memo(({ open, hoverExpanded }) => (
  <Flx
    fw
    center
    sx={{
      position: "relative",
      pt: 2,
      pb: 2,
      zIndex: 222,
      alignItems: "center",
      display: "flex",
      minHeight: "85px",
      flexShrink: 0,
      flexWrap: "wrap",
      justifyContent: "center",
    }}
  >
    <ToggleDashboardSidebarButton open={open} hoverExpanded={hoverExpanded} />
    {open && <DashboardLogo showSubtitle maxHeight={80} maxWidth={120} />}
  </Flx>
));

// ============================================================
// SidebarContent — scrollable link groups
// ============================================================
const SidebarContent = memo(
  ({ collapsed, topLinks, bottomLinks, onMouseEnter, onMouseLeave }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
          width: 4,
        },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "grey.300",
          borderRadius: 2,
        },
      }}
    >
      <LinksRenderer
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        collapsed={collapsed}
        links={topLinks}
      />
      <LinksRenderer
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        collapsed={collapsed}
        links={bottomLinks}
      />
    </Box>
  ),
);

// ============================================================
// LinksRenderer
// ============================================================
const LinksRenderer = ({ collapsed, links, onMouseEnter, onMouseLeave }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {links.map((group, idx) => (
        <Fragment key={idx}>
          {group?.divider === "top" && <Divider sx={{ my: 1 }} />}

          <Flx column>
            <LinkListGroup collapsed={collapsed} title={group.title}>
              {group.items.map((item) =>
                item.children ? (
                  <LinkDropdown
                    key={item.label}
                    collapsed={collapsed}
                    icon={item.icon}
                    label={item.label}
                    to={item.to}
                  >
                    {item.children.map((child) => (
                      <LinkListItem
                        key={child.label}
                        collapsed={collapsed}
                        to={child.to}
                        label={child.label}
                      />
                    ))}
                  </LinkDropdown>
                ) : item?.component ? (
                  item.component
                ) : (
                  <LinkListItem
                    key={item.label}
                    collapsed={collapsed}
                    icon={item.icon}
                    to={item.to}
                    onClick={item.onClick}
                    label={item.label}
                  />
                ),
              )}
            </LinkListGroup>
          </Flx>
          {group?.divider === "bottom" && <Divider sx={{ my: 1 }} />}
        </Fragment>
      ))}
    </Box>
  );
};

// ============================================================
// ToggleDashboardSidebarButton
// ============================================================
const ToggleDashboardSidebarButton = ({ open, hoverExpanded }) => {
  const [hover, setHover] = useState(false);
  const dispatch = useDispatch();

  const onToggle = useCallback(() => {
    dispatch(navSidebarToggle());
  }, [dispatch]);

  // When hover-expanded: show a "Pin" button
  if (hoverExpanded) {
    return (
      <Tooltip
        title="Pin sidebar open"
        placement="right"
        arrow
        enterDelay={800}
        leaveDelay={300}
        disableInteractive
      >
        <Box
          onClick={onToggle}
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: "10px",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: "28px",
            border: `1px solid ${grey[300]}`,
            height: "28px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "11px",
            fontWeight: 600,
            color: "#fff",
            justifyContent: "center",
            transition: "all 0.2s ease",
            whiteSpace: "nowrap",
            "&:hover": {
              background: grey[100],
            },
          }}
        >
          <PushPin sx={{ fontSize: 13, color: "primary.main" }} />
        </Box>
      </Tooltip>
    );
  }

  if (open) {
    return (
      <Tooltip
        title={open ? "Collapse" : "Expand"}
        placement="right"
        arrow
        disableInteractive
        enterDelay={800}
        leaveDelay={300}
      >
        <IconButton
          onClick={onToggle}
          size="small"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: "12px",
            width: "28px",
            height: "28px",
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {!hover && (
            <Suspense fallback={<div style={{ width: 20, height: 20 }} />}>
              <DashboardPanelSvg height={20} />
            </Suspense>
          )}
          {hover && (
            <KeyboardTab
              sx={{
                fontSize: 12,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.3s",
              }}
            />
          )}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <IconButton
      onClick={onToggle}
      size="small"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        position: "absolute",
        top: "50%",
        transform: "translate(-50%, -50%)",
        left: "50%",
        width: "28px",
        height: "28px",
        p: 0,
        display: "flex",
        justifyContent: "center",
        border: `1px solid ${grey[400]}`,
        borderRadius: "4px",
        alignItems: "center",
        color: (theme) => theme.palette.text.primary,
      }}
    >
      <MenuRounded className="thin-6" />
    </IconButton>
  );
};

// ============================================================
// MobileNavbar
// ============================================================
const MobileNavbar = ({ children }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const toggleOpen = useCallback(() => {
    setOpen((s) => !s);
  }, []);

  return (
    <>
      <Flx
        fw
        g={2}
        ac
        jb
        sx={{
          position: "relative",
          height: "50px",
          background: "#ffffff",
          pl: 1,
          pr: 1,
          zIndex: 222,
          flexWrap: "wrap",
          boxShadow:
            "rgba(0, 0, 0, 0.32) 0px 0px 1px, rgba(0, 0, 0, 0.08) 0px 0px 2px, rgba(0, 0, 0, 0.08) 0px 1px 3px",
        }}
      >
        <DashboardLogo showSubtitle maxHeight={30} maxWidth={85} />
        <IconButton onClick={toggleOpen}>
          {open ? <CloseRounded /> : <MenuRounded />}
        </IconButton>
      </Flx>
      <MobileNavDrawer open={open}>{children}</MobileNavDrawer>
    </>
  );
};

const MobileNavDrawer = ({ drawerWidth = "100%", open = true, children }) => (
  <Drawer
    sx={{
      width: drawerWidth,
      flexShrink: 0,
      zIndex: 221,
      "& .MuiDrawer-paper": {
        width: drawerWidth,
        boxSizing: "border-box",
      },
    }}
    variant="persistent"
    anchor="left"
    open={open}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        height: "100%",
        overflowY: "auto",
        pt: 6,
      }}
    >
      {children}
    </Box>
  </Drawer>
);

// ============================================================
// Link components
// ============================================================
const LinkListItem = ({ label, to, icon, onClick, collapsed }) => {
  const theme = useTheme();

  const itemStyles = useMemo(
    () => (navData) =>
      getItemStyles({
        theme,
        isActive: isNil(to) ? false : navData?.isActive,
        collapsed,
      }),
    [theme, to, collapsed],
  );

  return (
    <ListItem disablePadding>
      <Tooltip
        title={collapsed ? label : ""}
        placement="right"
        arrow
        disableInteractive
      >
        <NavLink to={to} end style={itemStyles} onClick={onClick}>
          {icon}
          {!collapsed && (
            <span
              style={{
                opacity: 1,
                transition: "opacity 0.15s ease",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </span>
          )}
        </NavLink>
      </Tooltip>
    </ListItem>
  );
};

const LinkListGroup = ({ title, children, collapsed }) => (
  <Flx
    column
    className="link-list-group"
    sx={{
      p: collapsed ? "4px" : 2,
      pt: collapsed || isNil(title) ? "8px" : "20px",
      a: { fontSize: "11px !important" },
      ".MuiSvgIcon-root": { fontSize: "14px" },
    }}
  >
    <GroupTitle collapsed={collapsed}>{title}</GroupTitle>
    <List className="link-list" sx={{ pb: 0, pt: 0 }}>
      {children}
    </List>
  </Flx>
);

const GroupTitle = ({ children, collapsed }) => {
  if (collapsed || isNil(children)) return null;

  return (
    <Typography
      className="link-list-group-title"
      textTransform="uppercase"
      fontSize="11px"
      fontWeight={500}
      color={grey[600]}
      sx={{ mb: 0.5 }}
    >
      {children}
    </Typography>
  );
};

const LinkDropdown = ({
  label,
  icon,
  children,
  collapsed,
  to,
  defaultCollapsed = true,
}) => {
  const [open, setOpen] = useState(defaultCollapsed);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isChildActive = useMemo(() => {
    if (!collapsed) return false;
    const childRoutes = [];
    if (Array.isArray(children)) {
      children.forEach((child) => {
        if (child?.props?.to) childRoutes.push(child.props.to);
      });
    } else if (children?.props?.to) {
      childRoutes.push(children.props.to);
    }
    if (to) childRoutes.push(to);
    return childRoutes.some((route) => location.pathname.startsWith(route));
  }, [collapsed, children, to, location.pathname]);

  const handleClick = useCallback(() => {
    if (collapsed) {
      if (to) navigate(to);
      return;
    }

    if (open) {
      setOpen(false);
    } else {
      setOpen(true);
      if (to) navigate(to);
    }
  }, [collapsed, open, to, navigate]);

  const buttonStyles = useMemo(
    () => ({
      ...getItemStyles({ theme, collapsed, isActive: isChildActive }),
    }),
    [theme, collapsed, isChildActive],
  );

  return (
    <>
      <ListItem disablePadding className="link-list-item">
        <Tooltip
          title={collapsed ? label : ""}
          placement="right"
          arrow
          disableInteractive
        >
          <ListItemButton
            className="link-list-item-button"
            onClick={handleClick}
            sx={buttonStyles}
          >
            {icon}
            {!collapsed && label}
            {!collapsed && (open ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </Tooltip>
      </ListItem>

      {!collapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              ml: 1.8,
              pl: 1,
              borderLeft: `1px solid ${grey[300]}`,
              a: { fontWeight: "400 !important" },
            }}
          >
            {children}
          </List>
        </Collapse>
      )}
    </>
  );
};

// ============================================================
// PropTypes
// ============================================================
DashboardLayout.propTypes = {
  open: PropTypes.bool,
  children: PropTypes.node,
  topLinks: PropTypes.array,
  bottomLinks: PropTypes.array,
};

NavRenderer.propTypes = {
  isMobile: PropTypes.bool,
  open: PropTypes.bool,
  topLinks: PropTypes.array,
  bottomLinks: PropTypes.array,
};

MobileNavbar.propTypes = {
  children: PropTypes.node,
};

MobileNavDrawer.propTypes = {
  drawerWidth: PropTypes.string,
  open: PropTypes.bool,
  children: PropTypes.node,
};

ToggleDashboardSidebarButton.propTypes = {
  open: PropTypes.bool,
  hoverExpanded: PropTypes.bool,
};

LinkListItem.propTypes = {
  label: PropTypes.string,
  to: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
  collapsed: PropTypes.bool,
};

LinkListGroup.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  collapsed: PropTypes.bool,
};

GroupTitle.propTypes = {
  children: PropTypes.node,
  collapsed: PropTypes.bool,
};

LinkDropdown.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.node,
  children: PropTypes.node,
  collapsed: PropTypes.bool,
  to: PropTypes.string,
  defaultCollapsed: PropTypes.bool,
};

export default DashboardLayout;
